import _, {union, without} from 'lodash';

import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {AppAction} from '../../appActions';
import {ApiTokenActionsEnum} from '../../data/apiTokens/apiTokensActions';
import {CustomFieldsActionsEnum} from '../../data/customFields/customFieldsActions';
import {LocalApplicationActionsEnum} from '../../data/localApplications/localApplicationsActions';
import {SettingsPanelActionsEnum} from './settingsPanelActions';
import {
  isSettingsResourcesCustomFieldsResource,
  isSettingsResourcesDevelopersResource,
  isSettingsResourcesSignaturesResource,
  isSettingsResourcesTagsResource,
  SettingsPanelResourceTypesEnum,
  SettingsPanelState,
} from './settingsPanelState';

export function settingsPanelReducer(state: SettingsPanelState, action: AppAction): SettingsPanelState {
  switch (action.type) {
    /*
     * Open Actions.
     */

    case SettingsPanelActionsEnum.OPEN_CUSTOM_FIELDS:
      return {
        ...state,
        resources: {
          resourceType: SettingsPanelResourceTypesEnum.CUSTOM_FIELDS,
        },
      };
    case SettingsPanelActionsEnum.OPEN_RULES:
      return {
        ...state,
        resources: {
          resourceType: SettingsPanelResourceTypesEnum.RULES,
        },
      };
    case SettingsPanelActionsEnum.OPEN_TAGS:
      return {
        ...state,
        resources: {
          resourceType: SettingsPanelResourceTypesEnum.TAGS,
          referenceResourceIds: [],
        },
      };
    case SettingsPanelActionsEnum.OPEN_TEAMMATE_ROOT:
      return {
        ...state,
        resources: {
          resourceType: SettingsPanelResourceTypesEnum.TEAMMATES,
        },
      };
    case SettingsPanelActionsEnum.OPEN_SIGNATURES:
      return {
        ...state,
        resources: {
          resourceType: SettingsPanelResourceTypesEnum.SIGNATURES,
          signatureIds: [],
        },
      };
    case SettingsPanelActionsEnum.OPEN_DEVELOPERS:
      if (isSettingsResourcesDevelopersResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          resourceType: SettingsPanelResourceTypesEnum.DEVELOPERS,
        },
      };

    case SettingsPanelActionsEnum.CLOSE_DEVELOPERS:
      return {
        ...state,
        // If the resources as developers, we can clear it.
        resources: isSettingsResourcesDevelopersResource(state.resources)
          ? {resourceType: SettingsPanelResourceTypesEnum.NONE}
          : state.resources,
      };

    /*
     * Other actions.
     */

    case SettingsPanelActionsEnum.TAGS_FETCHED: {
      if (!isSettingsResourcesTagsResource(state.resources)) {
        return state;
      }
      const newIds = _(action.payload.searchTags.tags)
        .flatten()
        .uniqBy((t) => t.id)
        .map((t) => t.id)
        .concat(state.resources.referenceResourceIds)
        .uniq()
        .value();
      return {
        ...state,
        resources: {
          ...state.resources,
          referenceResourceIds: newIds,
        },
      };
    }

    case SettingsPanelActionsEnum.FETCH_CUSTOM_FIELDS: {
      if (!isSettingsResourcesCustomFieldsResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          customFieldIds: [],
          selectedCustomFieldIds: [],
          status: AsyncStatusesEnum.LOADING,
        },
      };
    }
    case SettingsPanelActionsEnum.FETCH_CUSTOM_FIELDS_COMPLETED: {
      if (!isSettingsResourcesCustomFieldsResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          customFieldIds: union(state.resources.customFieldIds, action.payload.customFieldIds),
          status: AsyncStatusesEnum.LOADED,
        },
      };
    }
    case SettingsPanelActionsEnum.FETCH_CUSTOM_FIELDS_FAILED: {
      if (!isSettingsResourcesCustomFieldsResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          status: AsyncStatusesEnum.FAILED,
        },
      };
    }

    case SettingsPanelActionsEnum.ADD_SELECTED_CUSTOM_FIELD_IDS: {
      if (!isSettingsResourcesCustomFieldsResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          selectedCustomFieldIds: union(
            state.resources.selectedCustomFieldIds,
            action.payload.customFieldIds,
          ),
        },
      };
    }
    case SettingsPanelActionsEnum.REMOVE_SELECTED_CUSTOM_FIELD_IDS: {
      if (!isSettingsResourcesCustomFieldsResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          selectedCustomFieldIds: (state.resources.selectedCustomFieldIds ?? []).filter(
            (customFieldId) => !action.payload.customFieldIds.includes(customFieldId),
          ),
        },
      };
    }
    case SettingsPanelActionsEnum.REMOVE_CUSTOM_FIELD_IDS: {
      if (!isSettingsResourcesCustomFieldsResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          customFieldIds: without(state.resources.customFieldIds ?? [], ...action.payload.customFieldIds),
          selectedCustomFieldIds: without(
            state.resources.selectedCustomFieldIds ?? [],
            ...action.payload.customFieldIds,
          ),
        },
      };
    }
    case CustomFieldsActionsEnum.CUSTOM_FIELD_DELETED: {
      if (!isSettingsResourcesCustomFieldsResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          customFieldIds: without(state.resources.customFieldIds ?? [], action.payload),
          selectedCustomFieldIds: without(state.resources.selectedCustomFieldIds ?? [], action.payload),
        },
      };
    }
    case SettingsPanelActionsEnum.FETCH_SIGNATURES: {
      if (!isSettingsResourcesSignaturesResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          status: AsyncStatusesEnum.LOADING,
          signatureIds: [],
        },
      };
    }
    case SettingsPanelActionsEnum.FETCH_SIGNATURES_COMPLETED: {
      if (!isSettingsResourcesSignaturesResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          status: AsyncStatusesEnum.LOADED,
          signatureIds: action.payload.signatureIds,
        },
      };
    }
    case SettingsPanelActionsEnum.FETCH_SIGNATURES_FAILED: {
      if (!isSettingsResourcesSignaturesResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          status: AsyncStatusesEnum.FAILED,
        },
      };
    }

    case SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATIONS: {
      if (!isSettingsResourcesDevelopersResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          localApplicationIds: action.payload.shouldClearExisting ? [] : state.resources.localApplicationIds,
          localApplicationAsyncStatus: AsyncStatusesEnum.LOADING,
        },
      };
    }
    case SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATIONS_COMPLETED: {
      if (!isSettingsResourcesDevelopersResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          localApplicationIds: union(state.resources.localApplicationIds, action.payload.localApplicationIds),
          localApplicationsAsyncStatus: AsyncStatusesEnum.LOADED,
          // TODO: Remove this for a shared constant somewhere.
          canLoadMoreLocalApplications: action.payload.localApplicationIds.length === 50,
        },
      };
    }
    case SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATIONS_FAILED: {
      if (!isSettingsResourcesDevelopersResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          localApplicationsAsyncStatus: AsyncStatusesEnum.FAILED,
        },
      };
    }

    case SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATION: {
      return {
        ...state,
        resources: {
          ...(isSettingsResourcesDevelopersResource(state.resources)
            ? state.resources
            : {resourceType: SettingsPanelResourceTypesEnum.DEVELOPERS}),
          localApplicationId: action.payload.localApplicationId,
          localApplicationAsyncStatus: AsyncStatusesEnum.LOADING,
        },
      };
    }
    case SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATION_COMPLETED: {
      if (!isSettingsResourcesDevelopersResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          localApplicationAsyncStatus: AsyncStatusesEnum.LOADED,
        },
      };
    }
    case SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATION_FAILED: {
      if (!isSettingsResourcesDevelopersResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          localApplicationAsyncStatus: AsyncStatusesEnum.FAILED,
        },
      };
    }

    case SettingsPanelActionsEnum.FETCH_API_TOKENS: {
      if (!isSettingsResourcesDevelopersResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          apiTokenIds: [],
          apiTokenAsyncStatus: AsyncStatusesEnum.LOADING,
        },
      };
    }
    case SettingsPanelActionsEnum.FETCH_API_TOKENS_COMPLETED: {
      if (!isSettingsResourcesDevelopersResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          apiTokenIds: union(state.resources.apiTokenIds, action.payload.apiTokenIds),
          apiTokenAsyncStatus: AsyncStatusesEnum.LOADED,
        },
      };
    }
    case SettingsPanelActionsEnum.FETCH_API_TOKENS_FAILED: {
      if (!isSettingsResourcesDevelopersResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          apiTokenAsyncStatus: AsyncStatusesEnum.FAILED,
        },
      };
    }
    case SettingsPanelActionsEnum.API_TOKEN_CREATED: {
      if (!isSettingsResourcesDevelopersResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          apiTokenIds: union([action.payload.apiTokenId], state.resources.apiTokenIds),
        },
      };
    }
    case SettingsPanelActionsEnum.LOCAL_APPLICATION_CREATED: {
      if (!isSettingsResourcesDevelopersResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          localApplicationIds: union(
            [action.payload.localApplicationId],
            state.resources.localApplicationIds,
          ),
          // We can treat this as loaded already since we just created it.
          localApplicationId: action.payload.localApplicationId,
          localApplicationsAsyncStatus: AsyncStatusesEnum.LOADED,
        },
      };
    }

    case LocalApplicationActionsEnum.LOCAL_APPLICATION_DELETED: {
      if (!isSettingsResourcesDevelopersResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          // If they are viewing the local application, set it as undefined.
          localApplicationId:
            state.resources.localApplicationId === action.payload.localApplicationId
              ? undefined
              : state.resources.localApplicationId,
          // Remove the local application id from the list.
          localApplicationIds: [...(state.resources.localApplicationIds ?? [])].filter(
            (localApplicationId) => localApplicationId !== action.payload.localApplicationId,
          ),
        },
      };
    }

    case ApiTokenActionsEnum.API_TOKEN_DELETED: {
      if (!isSettingsResourcesDevelopersResource(state.resources)) {
        return state;
      }
      return {
        ...state,
        resources: {
          ...state.resources,
          apiTokenIds: [...(state.resources.apiTokenIds ?? [])].filter(
            (tokenId) => tokenId !== action.payload,
          ),
        },
      };
    }

    default:
      return state;
  }
}
